'use client';

import { c as _c } from "react/compiler-runtime";
import { appendLPSourceToUrl } from '~/components/Link/src/LinkWithSource';
import { ListingCard } from '~/components/ListingCard';
import { Markdown } from '~/components/Markdown';
import { PromoBadge } from '~/components/PromoBadge';
import { getLovableUrlForBlueprint } from '~/constants';
import { OVERVIEW_EXPERIMENT_LP_SLUG, OVERVIEW_EXPERIMENT_PATH, OVERVIEW_EXPERIMENT_VARIANTS } from '~/constants/overviewExperiment';
import { useCurrencyContext } from '~/contexts/Currencies';
import { useOverviewExperimentVariant } from '~/hooks/useOverviewExperimentVariant';
import { usePathname } from '~/i18n/navigation';
import { trackBlueprintClicked } from '~/utils';
import { blueprintFromPrice } from '~/utils/blueprintHelpers/blueprintFromPrice';
import { joinPlaceNames } from '~/utils/blueprintHelpers/joinPlaceNames';
import styles from './TripCard.module.css';
import type { BlueprintCardProps } from './TripCard.types';
import { TripCardFooter } from './TripCardFooter';
import { TripCardPlaces } from './TripCardPlaces';
const withLp = (url: string, lp?: string) => {
  if (!lp) return url;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}lp=${lp}`;
};
const BlueprintCard = t0 => {
  const $ = _c(60);
  const {
    trip,
    showPromoBadge
  } = t0;
  const {
    description,
    shortDescription,
    blueprintId,
    image,
    title,
    duration,
    pricePerNight,
    placesLinked
  } = trip;
  const pathname = usePathname() || "";
  const {
    convertPriceInGBP
  } = useCurrencyContext();
  const variant = useOverviewExperimentVariant();
  let lovableUrl;
  let lp;
  let t1;
  let urlWithSource;
  if ($[0] !== blueprintId || $[1] !== pathname || $[2] !== title || $[3] !== variant) {
    lovableUrl = pathname === OVERVIEW_EXPERIMENT_PATH ? getLovableUrlForBlueprint(blueprintId) : undefined;
    lp = lovableUrl ? OVERVIEW_EXPERIMENT_LP_SLUG : undefined;
    let t2;
    if ($[8] !== blueprintId || $[9] !== lp || $[10] !== pathname || $[11] !== title) {
      const url = withLp(`/journey-template/${blueprintId}?title=${encodeURIComponent(title)}`, lp);
      t2 = appendLPSourceToUrl(pathname, url);
      $[8] = blueprintId;
      $[9] = lp;
      $[10] = pathname;
      $[11] = title;
      $[12] = t2;
    } else {
      t2 = $[12];
    }
    urlWithSource = t2;
    t1 = lovableUrl && variant === OVERVIEW_EXPERIMENT_VARIANTS.intermediate ? withLp(lovableUrl, lp) : undefined;
    $[0] = blueprintId;
    $[1] = pathname;
    $[2] = title;
    $[3] = variant;
    $[4] = lovableUrl;
    $[5] = lp;
    $[6] = t1;
    $[7] = urlWithSource;
  } else {
    lovableUrl = $[4];
    lp = $[5];
    t1 = $[6];
    urlWithSource = $[7];
  }
  const overviewUrl = t1;
  let t2;
  if ($[13] !== overviewUrl || $[14] !== urlWithSource) {
    t2 = overviewUrl ? {
      href: overviewUrl,
      isExternal: true,
      target: "_self" as const
    } : {
      href: urlWithSource
    };
    $[13] = overviewUrl;
    $[14] = urlWithSource;
    $[15] = t2;
  } else {
    t2 = $[15];
  }
  const linkProps = t2;
  let t3;
  if ($[16] !== blueprintId || $[17] !== lovableUrl || $[18] !== lp) {
    t3 = lovableUrl ? {
      "data-ua-click": "blueprint_card_click",
      "data-lp-source": lp,
      "data-blueprint-id": blueprintId
    } : {};
    $[16] = blueprintId;
    $[17] = lovableUrl;
    $[18] = lp;
    $[19] = t3;
  } else {
    t3 = $[19];
  }
  const trackingAttrs = t3;
  let t4;
  if ($[20] !== convertPriceInGBP || $[21] !== duration || $[22] !== pricePerNight) {
    const fromPrice = blueprintFromPrice(pricePerNight, duration);
    t4 = fromPrice ? convertPriceInGBP(fromPrice) : null;
    $[20] = convertPriceInGBP;
    $[21] = duration;
    $[22] = pricePerNight;
    $[23] = t4;
  } else {
    t4 = $[23];
  }
  const price = t4;
  let t5;
  if ($[24] !== blueprintId || $[25] !== linkProps.href || $[26] !== title || $[27] !== trip.allTags || $[28] !== trip.tags) {
    t5 = () => trackBlueprintClicked({
      blueprintId,
      title,
      tags: trip.allTags ?? trip.tags,
      sectionTag: trip.allTags ? trip.tags[0]?.tagId : undefined,
      url: linkProps.href
    });
    $[24] = blueprintId;
    $[25] = linkProps.href;
    $[26] = title;
    $[27] = trip.allTags;
    $[28] = trip.tags;
    $[29] = t5;
  } else {
    t5 = $[29];
  }
  const handleClick = t5;
  let t6;
  if ($[30] !== image) {
    t6 = image?.responsiveImage ? [{
      data: image.responsiveImage
    }] : [];
    $[30] = image;
    $[31] = t6;
  } else {
    t6 = $[31];
  }
  let t7;
  if ($[32] !== showPromoBadge) {
    t7 = showPromoBadge ? <PromoBadge /> : null;
    $[32] = showPromoBadge;
    $[33] = t7;
  } else {
    t7 = $[33];
  }
  let t8;
  if ($[34] !== t6 || $[35] !== t7) {
    t8 = <div className={styles.gallerySlot}><ListingCard.Gallery images={t6} tag={t7} /></div>;
    $[34] = t6;
    $[35] = t7;
    $[36] = t8;
  } else {
    t8 = $[36];
  }
  let t9;
  if ($[37] !== handleClick || $[38] !== linkProps || $[39] !== title || $[40] !== trackingAttrs) {
    t9 = <ListingCard.Title as="h3"><ListingCard.Link {...linkProps} {...trackingAttrs} onClick={handleClick}>{title}</ListingCard.Link></ListingCard.Title>;
    $[37] = handleClick;
    $[38] = linkProps;
    $[39] = title;
    $[40] = trackingAttrs;
    $[41] = t9;
  } else {
    t9 = $[41];
  }
  let t10;
  if ($[42] !== placesLinked) {
    t10 = joinPlaceNames(placesLinked);
    $[42] = placesLinked;
    $[43] = t10;
  } else {
    t10 = $[43];
  }
  let t11;
  if ($[44] !== t10) {
    t11 = <TripCardPlaces places={t10} />;
    $[44] = t10;
    $[45] = t11;
  } else {
    t11 = $[45];
  }
  let t12;
  if ($[46] !== description || $[47] !== shortDescription) {
    t12 = (shortDescription || description) && <Markdown className={styles.markdown}>{shortDescription || description}</Markdown>;
    $[46] = description;
    $[47] = shortDescription;
    $[48] = t12;
  } else {
    t12 = $[48];
  }
  let t13;
  if ($[49] !== duration || $[50] !== price) {
    t13 = <TripCardFooter price={price} nights={duration} />;
    $[49] = duration;
    $[50] = price;
    $[51] = t13;
  } else {
    t13 = $[51];
  }
  let t14;
  if ($[52] !== t11 || $[53] !== t12 || $[54] !== t13 || $[55] !== t9) {
    t14 = <ListingCard.Content><div className={styles.tripCardContentInner}>{t9}{t11}{t12}{t13}</div></ListingCard.Content>;
    $[52] = t11;
    $[53] = t12;
    $[54] = t13;
    $[55] = t9;
    $[56] = t14;
  } else {
    t14 = $[56];
  }
  let t15;
  if ($[57] !== t14 || $[58] !== t8) {
    t15 = <ListingCard variant="trip">{t8}{t14}</ListingCard>;
    $[57] = t14;
    $[58] = t8;
    $[59] = t15;
  } else {
    t15 = $[59];
  }
  return t15;
};
export { BlueprintCard };